import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { loadingScrimColor } from '../context/AppUiContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { ReactComponent as ShiftyLoading } from '../ShiftyPM_Loading.svg';

/**
 * Full-viewport dimmed overlay while async work blocks the UI.
 * Uses animated Shifty SVG by default. Under `WorkspaceProvider` (Dashboard),
 * uses company logo + spinner when portal branding is enabled and a logo URL exists.
 *
 * @param {boolean} open
 * @param {boolean} [forceShifty] — Always use Shifty loading art (e.g. Super Admin).
 */
export default function LoadingLockScreen({ open, forceShifty = false }) {
  const theme = useTheme();
  const branding = useWorkspace().workspace;
  const logoUrl =
    typeof branding?.logoUrl === 'string' && branding.logoUrl.trim() ? branding.logoUrl.trim() : '';
  const useBrand =
    !forceShifty && Boolean(branding?.portalBrandingEnabled) && Boolean(logoUrl);

  if (!open) return null;

  return (
    <Box
      role="progressbar"
      aria-busy="true"
      aria-label="Loading"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: (t) => t.zIndex.modal + 500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: loadingScrimColor(theme.palette.mode),
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      {useBrand ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            maxWidth: 'min(92vw, 360px)',
            px: 2,
          }}
        >
          <Box
            component="img"
            src={logoUrl}
            alt={branding?.name ? `${branding.name} logo` : 'Company logo'}
            sx={{
              width: '100%',
              maxHeight: { xs: 72, sm: 96 },
              objectFit: 'contain',
              filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.35))',
              animation: 'workspaceBrandPulse 1.6s ease-in-out infinite',
              '@keyframes workspaceBrandPulse': {
                '0%, 100%': { opacity: 0.92 },
                '50%': { opacity: 1 },
              },
            }}
          />
          <CircularProgress size={44} thickness={4} sx={{ color: 'primary.main' }} />
        </Box>
      ) : (
        <Box
          sx={{
            width: { xs: 112, sm: 140 },
            height: { xs: 112, sm: 140 },
            filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.35))',
            '& svg': { width: '100%', height: '100%', display: 'block' },
          }}
        >
          <ShiftyLoading />
        </Box>
      )}
    </Box>
  );
}
